<template>
  <form class="form-invoice" v-on:submit.prevent="onSubmit">
    <div
      class="form-invoice__table"
      v-bind:class="positionTableClasses"
    >
      <popover-menu v-if="disabled">
        <template v-slot:trigger="{ listenerObject }">
          <div class="form-invoice__table__cell-settings-mob">
            <button class="form-invoice__buttons-settings form-invoice__icons-settings " type="button" v-on="listenerObject" />
          </div>
        </template>
        <form-table-settings
          v-bind:columns="columnSettings"
          v-on:change="onChangeColumnSettings"
        />
      </popover-menu>
      <table-head v-bind:columns="visibleColumns" v-bind:disabled="disabled">
        <div slot="discount" class="form-invoice__table__cell discount">
          Скидка
          <switch-discount-type
            v-if="!disabled"
            v-bind:disabled="disabled"
            v-bind:value="initialDiscountType"
            v-on:input="onInputDiscountType"
          />
        </div>
        <div
          v-if="isShowComplaint"
          slot="complaint"
          class="form-invoice__table__cell complaint"
        >Рекламация</div>
        <div slot="status" class="form-invoice__table__cell status">Статус</div>
        <template slot="settings">
          <popover-menu>
            <template v-slot:trigger="{ listenerObject }">
              <div class="form-invoice__table__cell settings">
                <button class="form-invoice__icons-settings" type="button" v-on="listenerObject" />
              </div>
            </template>
            <form-table-settings
              v-bind:columns="columnSettings"
              v-on:change="onChangeColumnSettings"
            />
          </popover-menu>
        </template>
      </table-head>
      <row-invoice
        class="form-invoice__table__row"
        v-for="(position, index) in initialPositions"
        v-bind:key="position.id || position.key"
        v-bind:amount-in-kop="position.getAmount().toKop()"
        v-bind:columns="visibleColumns"
        v-bind:disabled="disabled"
        v-bind:initial-by-vhi-policy="position.byVHIPolicy"
        v-bind:initial-category-name="position.categoryName"
        v-bind:initial-complaint="position.complaint"
        v-bind:show-complaint-cell="isShowComplaint"
        v-bind:initial-cost="position.cost"
        v-bind:initial-count="position.count"
        v-bind:initial-discount-type="position.discount.getType()"
        v-bind:initial-discount="position.discount.getValue()"
        v-bind:show-discount-postfix="isShowDiscountPostfix"
        v-bind:initial-final-price="position.getFinalPrice().toKop()"
        v-bind:initial-item-type="position.itemType"
        v-bind:initial-service-id="position.serviceId"
        v-bind:initial-service-name="position.serviceName"
        v-bind:initial-vat="position.vat"
        v-bind:initial-service-clinic-code="position.serviceClinicCode"
        v-bind:initial-service-dental-code="position.serviceDentalCode"
        v-bind:input-errors="positionInputErrors[position.id] || []"
        v-bind:services="services"
        v-bind:status="position.status"
        v-on:input-by-vhi-policy="onInputByVhiPolicy(index, $event)"
        v-on:input-complaint="onInputComplaint(index, $event)"
        v-on:input-count="onInputCount(index, $event)"
        v-on:input-discount="onInputDiscount(index, $event)"
        v-on:input-service-id="onInputServiceId(index, $event)"
        v-on:remove="onRemovePosition(index)"
      />
    </div>
    <div class="form-invoice__table__controls">
      <div class="form-invoice__table__controls__aggregate">
        <span class="form-invoice__table__controls__aggregate__field">Итого со скидкой:</span>
        <the-rub
          class="form-invoice__table__controls__aggregate__rub"
          currency="руб"
          show-kop="auto"
          v-bind:kop="aggregateTotal.final"
        />
        <template v-if="!isCreation">
          <span
            v-if="hasDebt"
            class="form-invoice__table__controls__aggregate__field"
          >Долг:</span>
          <the-rub
            v-if="hasDebt"
            class="form-invoice__table__controls__aggregate__rub"
            currency="руб"
            show-kop="auto"
            v-bind:kop="debtByKop"
          />
          <span
            v-if="hasPayment"
            class="form-invoice__table__controls__aggregate__field"
          >Оплачено:</span>
          <div v-if="hasPayment" class="form-invoice__table__controls__aggregate__details">
            <the-rub
              v-if="hasPaymentCash"
              title="Оплачено наличными"
              class="form-invoice__table__controls__aggregate__details__cash"
              currency="руб"
              show-kop="auto"
              v-bind:kop="paymentCash"
            />
            <the-rub
              v-if="hasPaymentCashless"
              title="Безналичный способ оплаты"
              class="form-invoice__table__controls__aggregate__details__cashless"
              currency="руб"
              show-kop="auto"
              v-bind:kop="paymentCashless"
            />
          </div>
          <span
            v-if="hasPaymentAdvance"
            class="form-invoice__table__controls__aggregate__field"
          >Списание с аванса:</span>
          <the-rub
            v-if="hasPaymentAdvance"
            class="form-invoice__table__controls__aggregate__rub"
            currency="руб"
            show-kop="auto"
            v-bind:kop="paymentAdvance"
          />
          <span
            v-if="hasRefund"
            class="form-invoice__table__controls__aggregate__field"
          >Возврат:</span>
          <the-rub
            v-if="hasRefund"
            class="form-invoice__table__controls__aggregate__rub"
            currency="руб"
            show-kop="auto"
            v-bind:kop="aggregateTotal.refund"
          />
        </template>
      </div>
    </div>
    <slot name="buttons" />
  </form>
</template>

<script>
import FormTableSettings from '@/components/FormTableSettings.vue';
import PopoverMenu from '@/components/PopoverMenu.vue';
import RowInvoice from '@/components/RowInvoice.vue';
import SwitchDiscountType from '@/components/SwitchDiscountType.vue';
import TheRub from '@/components/TheRub.vue';
import spreadDiscountMixin from '@/mixins/spreadDiscountMixin';

/**
 * @description Название события изменения позиции.
 * @type {string}
 */
const NAME_EVENT_CHANGE_POSITION = 'change-position';

/**
 * @description Название события удаления позиции.
 * @type {string}
 */
const NAME_EVENT_REMOVE_POSITION = 'remove-position';

/**
 * @description Название события отправки формы.
 * @type {string}
 */
const NAME_EVENT_SUBMIT = 'submit';

/**
 * @description Название события вода типа скидки.
 * @type {string}
 */
const NAME_EVENT_INPUT_DISCOUNT_TYPE = 'input-discount-type';

/**
 * @description Компонент хедера таблицы.
 */
const TableHead = {
  functional: true,
  name: 'TableHead',
  props: {
    columns: { required: true, type: Array },
    disabled: { require: true, type: Boolean },
  },
  render(createElement, { props, scopedSlots }) {
    return createElement(
      'div',
      { staticClass: 'form-invoice__table__head' },
      [
        ...props.columns.map((column) => {
          if (scopedSlots[column.id]) {
            return scopedSlots[column.id]();
          }
          return createElement(
            'div',
            { staticClass: `form-invoice__table__cell ${column.id}`, key: column.id },
            column.name,
          );
        }),
        scopedSlots.complaint && scopedSlots.complaint(),
        !props.disabled && createElement('div', { staticClass: 'form-invoice__table__cell remove', key: 'delete' }),
        props.disabled && scopedSlots.settings(),
      ],
    );
  },
};

/**
 * @description Форма счета.
 * @type {any}
 */
export default {
  components: {
    FormTableSettings,
    PopoverMenu,
    RowInvoice,
    SwitchDiscountType,
    TableHead,
    TheRub,
  },
  computed: {
    hasPayment() {
      return this.hasPaymentCashless || this.hasPaymentCash;
    },
    hasPaymentCashless() {
      return this.paymentCashless > 0;
    },
    hasPaymentCash() {
      return this.paymentCash > 0;
    },
    hasPaymentAdvance() {
      return this.paymentAdvance > 0;
    },
    hasRefund() {
      return this.aggregateTotal.refund > 0;
    },
    hasDebt() {
      return this.debtByKop > 0;
    },
    /**
     * @description Суммарные итоги.
     * @returns {Object}
     */
    aggregateTotal() {
      const initial = {
        amount: 0,
        discount: 0,
        final: 0,
        amountWithoutFreeServices: 0,
        discountWithoutFreeServices: 0,
        refund: 0,
        debtAfterRefund: 0,
      };
      return this.initialPositions.reduce(
        (total, position) => ({
          amount: total.amount + position.getAmount().toKop(),
          discount: total.discount + position.getDiscount().toKop(),
          final: total.final + position.getFinalPrice().toKop(),
          amountWithoutFreeServices: total.amountWithoutFreeServices
            + (position.isFullDiscounted() ? 0 : position.getAmount().toKop()),
          discountWithoutFreeServices: total.discountWithoutFreeServices
            + (position.isFullDiscounted() ? 0 : position.getDiscount().toKop()),
          refund: total.refund + position.getRefund().toKop(),
          debtAfterRefund: total.debtAfterRefund
            + (
              position.getRefund().toKop() > 0
                ? position.getAmount().toKop() - position.getRefund().toKop()
                : 0
            ),
        }),
        initial,
      );
    },
    /**
     * @description Долг в копейках.
     * @type {number}
     */
    debtByKop() {
      const kop = this.aggregateTotal.final - this.paid - this.aggregateTotal.debtAfterRefund;
      return kop < 0 ? 0 : kop;
    },
    /**
     * @description Позиции с нарушением ограничений.
     * @type {any}
     */
    positionInputErrors() {
      const values = Object
        .keys(this.inputErrors || {})
        .filter((key) => key.startsWith('positions'))
        .map((key) => {
          const [, positionId] = key.split('.');
          return [Number(positionId), this.inputErrors[key]];
        });
      return Object.fromEntries(values);
    },
    /**
     * @description Показывать ли столбец с рекламацией.
     * @type {boolean}
     */
    isShowComplaint() {
      return this.disabled
        ? this.initialPositions.some((position) => position?.complaint)
        : true;
    },
    /**
     * @description Показывать ли постфикс скидки.
     * @type {boolean}
     */
    isShowDiscountPostfix() {
      return this.disabled
        ? this.initialPositions.some((position) => position?.discount.getValue() > 0)
        : true;
    },
    /**
     * @description Видимые столбцы.
     * @returns {any[]}
     */
    visibleColumns() {
      if (this.disabled !== true) {
        return this.columns;
      }
      return this.columns.filter((candidate) => candidate.is_visible);
    },
    positionTableClasses() {
      const classes = {
        creation: this.isCreation,
        'not-edit': this.disabled,
        'not-complaint': !this.isShowComplaint,
      };
      const index = this.visibleColumns.findIndex((candidate) => candidate.id === 'service');
      if (index > 0) {
        classes[`before-${index}`] = true;
      }
      return classes;
    },
  },
  methods: {
    /**
     * @description Отправить событие изменения позиции.
     * @param {number}   index
     * @param {Position} value
     * @returns {void}
     */
    emitChangePosition(index, value) {
      this.$emit(NAME_EVENT_CHANGE_POSITION, index, value);
    },
    /**
     * @description Обработать удаление позиции.
     * @param {number} index
     * @returns {void}
     */
    onRemovePosition(index) {
      this.$emit(NAME_EVENT_REMOVE_POSITION, index);
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      this.$emit(NAME_EVENT_SUBMIT);
    },
    onInputDiscount(index, value) {
      const position = this.initialPositions[index];
      const discount = position.discount.setValue(value);
      this.emitChangePosition(index, position.setDiscount(discount));
    },
    onInputCount(index, value) {
      const position = this.initialPositions[index];
      this.emitChangePosition(index, position.setCount(value));
    },
    onInputByVhiPolicy(index, value) {
      const position = this.initialPositions[index];
      this.emitChangePosition(index, position.setByVHIPolicy(value));
    },
    onInputComplaint(index, value) {
      const position = this.initialPositions[index];
      const byVHIPolicy = value ? false : position.byVHIPolicy;
      this.emitChangePosition(index, position.setComplaint(value, byVHIPolicy));
    },
    onInputServiceId(index, value) {
      const position = this.initialPositions[index];
      const service = this.services.find((candidate) => candidate.id === value);
      this.emitChangePosition(
        index,
        position.setService(
          value,
          service.name,
          service.price,
          service.vat,
          service.item_type,
          service.category_name,
        ),
      );
    },
    onInputDiscountType(value) {
      this.$emit(NAME_EVENT_INPUT_DISCOUNT_TYPE, value);
    },
    onTotalDiscountChanged(value) {
      const rows = this.initialPositions.filter((candidate) => !candidate.isFullDiscounted());
      if (rows.length > 0) {
        const discounts = this.spreadDiscount(
          this.initialDiscountType,
          value,
          rows.map((candidate) => ({ amount: candidate.getAmount() })),
          this.aggregateTotal.amountWithoutFreeServices,
        );
        rows.forEach((candidate, index) => {
          const rowIndex = this.initialPositions.indexOf(candidate);
          const discount = candidate.discount.setValue(discounts[index]);
          this.emitChangePosition(rowIndex, candidate.setDiscount(discount));
        });
      }
    },
    onChangeColumnSettings(columns) {
      this.$emit('change-column-settings', columns);
    },
  },
  mixins: [spreadDiscountMixin],
  props: {
    /**
     * @description Столбцы.
     */
    columns: { required: true, type: Array },
    /**
     * @description Настройки столбцов.
     */
    columnSettings: { required: true, type: Array },
    /**
     * @description Оплачено авансом.
     * @type {Number}
     */
    paymentAdvance: { default: () => 0, type: Number },
    /**
     * @description Оплачено наличными.
     * @type {Number}
     */
    paymentCash: { default: () => 0, type: Number },
    /**
     * @description Оплачено безналичными.
     * @type {Number}
     */
    paymentCashless: { default: () => 0, type: Number },
    /**
     * @description Деактивирована.
     * @type {boolean}
     */
    disabled: { default: () => true, type: Boolean },
    /**
     * @description Тип скидки.
     * @type {String}
     */
    initialDiscountType: { required: true, type: String },
    /**
     * @description Позиции.
     * @type {Position[]}
     */
    initialPositions: { default: () => [], type: Array },
    /**
     * @description Нарушения ограничений.
     * @type {any}
     */
    inputErrors: { default: () => {}, type: Object },
    /**
     * @description Создание.
     * @type {boolean}
     */
    isCreation: { type: Boolean, default: false },
    /**
     * @description Оплачено.
     * @type {number}
     */
    paid: { default: () => 0, type: Number },
    /**
     * @description Оплаченная сумма.
     * @type {number}
     */
    paymentAmount: { default: 0, type: Number },
    /**
     * @description Услуги.
     * @type {any[]}
     */
    services: { type: Array, required: true },
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";
.form-invoice {
  width: 100%;
  &__buttons {
    &-add {
      @include buttons.btn-transparent(100%, 40px, 1rem);
      @include buttons.btn-transparent-add(1rem);
    }
    &-settings {
      display: none;
    }
    &-print {
      @include buttons.btn-transparent(100%, 48px, 16px);
      padding: 0;
      margin: 0;
      &:before {
        content: "";
        background-image: url("@/assets/img/print.svg");
        background-repeat: no-repeat;
        background-size: contain;
        width: 24px;
        height: 24px;
        margin: 0 8px 0 0;
      }
    }
    &-blue {
      @include buttons.btn-blue(100%, 48px, 1rem);
      &.add {
        @include buttons.btn-blue-add(1rem);
        max-width: 299px;
      }
      &:disabled {
        background-color: var(--unfocus-color);
        border: 1px solid var(--unfocus-color);
        color: white;
      }
    }
    &-cancel {
      @include buttons.btn-transparent(100%, 48px, 1rem);
    }
  }
  &__icons {
    &-settings {
      background-image: url("@/assets/img/settings-blue.svg");
      background-size: contain;
      background-repeat: no-repeat;
      width: 18px;
      height: 18px;
      padding: 0;
      margin: 0;
      cursor: pointer;
    }
    &__remove {
      display: block;
      background-image: url("@/assets/img/exit-grey.svg");
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      width: 12px;
      height: 12px;
      margin-inline: auto;
      cursor: pointer;
      &:hover {
        background-image: url("@/assets/img/exit-blue.svg");
      }
    }
  }
  &__table {
    counter-reset: counterServices;
    margin: 25px 0 0 0;
    overflow-x: auto;
    &__cell {
      transition: color var(--animate-duration);
      padding: 12px 8px;
      display: flex;
      align-items: center;
      &.name {
        .v-input {
          padding: 0 10px 5px 10px;
        }
        .v-input__control > .v-input__slot {
          &::before,
          &::after {
            border: none !important;
          }
          .v-select__slot > input {
            padding: 0 !important;
          }
        }
      }
      &.number {
        white-space: nowrap;
        & > input {
          width: 100%;
          &:focus {
            color: #212529;
            border-bottom: 1px solid #0489e0;
            outline: 0;
          }
          &:disabled {
            color: #212529;
            cursor: default;
          }
        }
      }
      &.checkbox {
        justify-self: center;
        transform: translateY(2px);
        & span::before {
          margin: 0 !important;
        }
        span.checkbox__title {
          cursor: inherit !important;
        }
      }
      &.discount {
        gap: 5px;
      }
      &.payment {
        justify-self: center;
        & span::before {
          margin: 0 !important;
        }
        & input:disabled:checked + span::before {
          background-color: var(--unfocus-color);
          border-color: var(--unfocus-color);
        }
        & input:disabled + span::before {
          background-color: #90a0b744;
          border-color: var(--unfocus-color);
        }
      }
      &.discount {
        & > input {
          background-color: white;
          border: 1px solid #0489e0;
          border-radius: 4px;
        }
      }
      &.status {
        white-space: nowrap;
      }
      &.complaint {
        justify-content: center;
      }
      &.remove {
        grid-column: -2;
        justify-content: center;
      }
    }
    &.creation {
      .form-invoice__table {
        &__head, &__row {
          grid-template-columns: minmax(100px, 1fr) repeat(auto-fit, 8%) 40px;
        }
        &__cell.payment, &__cell.status {
          display: none;
        }
      }
    }
    &.not-edit {
      &.before-1 {
        .form-invoice__table {
          &__head, &__row {
            grid-template-columns: minmax(130px, 8%) minmax(200px, 1fr) repeat(auto-fit, 7%) 40px;
          }
        }
      }
      &.before-2 {
        .form-invoice__table {
          &__head, &__row {
            grid-template-columns: repeat(2, minmax(130px, 8%)) minmax(200px, 1fr) repeat(auto-fit, 7%) 40px;
          }
        }
      }
      .form-invoice__table {
        &__head, &__row {
          grid-template-columns: minmax(200px, 1fr) repeat(auto-fit, 7%) 40px;
          @media screen and (min-width: 881px) {
            min-width: 1400px;
          }
        }
        &__row {
          &:hover {
            background: none;
          }
        }
        &__cell.discount {
          & > input {
            width: min(100%, 76px);
            border-color: var(--unfocus-color);;
          }
        }
        &__cell.settings {
          justify-content: center;
          grid-column: -2;
        }
        &__cell-settings-mob {
          display: flex;
          justify-content: flex-end;
          padding: 12px 8px;
        }
      }
    }
    &__head,
    &__row {
      display: grid;
      grid-template-columns: minmax(200px, 1fr) repeat(auto-fit, 8%) 40px;
      align-items: center;
      width: 100%;
      font-size: 14px;
      line-height: 17px;
      @media screen and (min-width: 881px) {
        min-width: 1400px;
      }
    }
    &.before-1 {
      .form-invoice__table {
        &__head, &__row {
          grid-template-columns: minmax(130px, 8%) minmax(200px, 1fr) repeat(auto-fit, 8%) 40px;
        }
      }
    }
    &.before-2 {
      .form-invoice__table {
        &__head, &__row {
          grid-template-columns: repeat(2, minmax(130px, 8%)) minmax(200px, 1fr) repeat(auto-fit, 8%) 40px;
        }
      }
    }
    &__head {
      font-weight: 700;
      border-bottom: 2px solid #0489e0;
    }
    &__row {
      text-align: left;
      border-bottom: 1px solid #eaeff3;
      &:hover {
        background: #eaeff3;
      }
    }
    &__controls {
      margin-top: 21px;
      width: 100%;
      display: grid;
      grid-template-columns: 226px 356px 1fr;
      column-gap: 9px;
      &__aggregate {
        display: grid;
        grid-template-columns: 1fr max-content;
        grid-column: 3;
        justify-content: flex-end;
        justify-items: end;
        justify-self: flex-end;
        text-align: right;
        column-gap: 107px;
        row-gap: 12px;
        &__details {
          display: flex;
          flex-direction: column;
          gap: 12px;
          min-width: calc(100% + 30px);
          color: #192a3e;
          font-weight: bold;
          &__cash,
          &__cashless {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 10px;
            &::before {
              background-position: center;
              background-size: contain;
              height: 20px;
              width: 20px;
            }
          }
          &__cash {
            &::before {
              content: '';
              background-image: url("@/assets/img/switch/rub-grey.svg");
              opacity: 0.7;
            }
          }
          &__cashless {
            &::before {
              content: '';
              background-image: url("@/assets/img/cashless-grey.svg");
            }
          }
        }
        &__field {
          justify-self: start;
          color: #707683;
          font-weight: bold;
          text-align: left;
        }
        &__rub {
          min-width: 100%;
          color: #192a3e;
          font-weight: bold;
          text-align: left;
        }
      }
    }
    &__input {
      .v-input__control {
        input {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }
}
@media screen and (max-width: 1080px) {
  .form-invoice__table {
    &.creation &__head,
    &.creation &__row {
      grid-template-columns: 1fr 122px 75px 140px 120px 100px 40px;
    }
    &__head,
    &__row {
      grid-template-columns: 1fr 122px 75px 140px 120px 100px 100px 40px;
    }
    &__controls {
      grid-template-columns: 1fr;
      row-gap: 10px;
      &__aggregate {
        grid-column: 1;
        width: 100%;
      }
    }
  }
}
@media screen and (max-width: 880px) {
  .form-invoice__table {
    &__head {
      display: none;
    }
    &__row,
    &.creation &__row,
    &.not-edit &__row,
    &.not-complaint.not-edit &__row {
      grid-template-columns: 1fr !important;
      grid-gap: 5px;
      margin: 0 0 20px 0;
    }
    &.not-edit {
      .form-invoice__table {
        &__cell.status {
          justify-content: space-between;
        }
        &__cell.discount {
          & > input {
            width: 100%;
            border: none;
          }
        }
      }
    }
    &__cell {
      display: flex;
      justify-content: space-between;
      text-align: right;
      &.checkbox, &.payment {
        justify-self: auto;
        &:empty {
          display: none;
        }
      }
      &.name {
        .v-select__slot > input {
          text-align: right;
        }
      }
      &.number > input {
        width: fit-content;
        max-width: 100px;
        margin-inline-start: auto;
        text-align: right;
      }

    }
    &__cell:before {
      content: attr(data-label);
      font-weight: 700;
      margin-right: 20px;
      white-space: nowrap;
    }
    &__controls {
      grid-template-columns: 1fr;
      row-gap: 10px;
      &__aggregate {
        grid-column: 1;
        column-gap: 37px;
        width: 100%;
      }
    }
  }
  .form-invoice__icons {
    &__remove {
      margin-inline-end: 0;
    }
  }
  .form-invoice__buttons-settings {
    display: block;
  }
}
</style>
